import { Head, router, useForm } from "@inertiajs/react"
import { FolderOpen, Plus, Pencil, Trash2 } from "lucide-react"
import { useMemo, useState } from "react"
import { AdminLayout } from "../components/AdminLayout"
import { SectionTabs } from "../components/SectionTabs"

const coursesTabs = [
  { label: "Courses", href: "/app/admin/courses" },
  { label: "Tracks", href: "/app/admin/learning_tracks" },
  { label: "Categories", href: "/app/admin/course_categories" },
]
import { Button } from "@/components/ui/button"
import { Card } from "@/components/ui/card"
import { Input } from "@/components/ui/input"
import { Label } from "@/components/ui/label"
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table"
import { SortableHeader } from "@/components/ui/sortable-header"
import {
  ResponsiveDialog,
  ResponsiveDialogContent,
  ResponsiveDialogHeader,
  ResponsiveDialogFooter,
  ResponsiveDialogTitle,
  ResponsiveDialogDescription,
} from "@/components/ui/responsive-dialog"
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog"

function CategoryDialog({ open, onOpenChange, category = null }) {
  const isEditing = !!category
  const form = useForm({
    name: category?.name || "",
    position: category?.position ?? 0,
  })

  const handleSubmit = (e) => {
    e.preventDefault()

    if (isEditing) {
      form.patch(`/app/admin/course_categories/${category.id}`, {
        preserveScroll: true,
        onSuccess: () => onOpenChange(false),
      })
    } else {
      form.post("/app/admin/course_categories", {
        preserveScroll: true,
        onSuccess: () => onOpenChange(false),
      })
    }
  }

  const handleClose = (isOpen) => {
    if (!isOpen) {
      form.reset()
      form.clearErrors()
    }
    onOpenChange(isOpen)
  }

  return (
    <ResponsiveDialog open={open} onOpenChange={handleClose}>
      <ResponsiveDialogContent className="sm:max-w-lg">
        <form onSubmit={handleSubmit}>
          <ResponsiveDialogHeader>
            <ResponsiveDialogTitle className="font-display text-xl">
              {isEditing ? "Edit Category" : "Add Category"}
            </ResponsiveDialogTitle>
            <ResponsiveDialogDescription>
              {isEditing
                ? "Update this course category."
                : "Create a new course category. The slug will be generated automatically."}
            </ResponsiveDialogDescription>
          </ResponsiveDialogHeader>

          <div className="space-y-4 py-4 px-4 sm:px-0">
            <div className="space-y-2">
              <Label htmlFor="name">Name</Label>
              <Input
                id="name"
                placeholder="Personal Development"
                className="bg-white border-zinc-300 text-zinc-900 placeholder:text-zinc-400"
                value={form.data.name}
                onChange={(e) => form.setData("name", e.target.value)}
              />
              {form.errors.name && (
                <p className="text-sm text-red-600">{form.errors.name}</p>
              )}
            </div>

            <div className="space-y-2">
              <Label htmlFor="position">Position</Label>
              <Input
                id="position"
                type="number"
                min="0"
                className="bg-white border-zinc-300 text-zinc-900"
                value={form.data.position}
                onChange={(e) =>
                  form.setData("position", parseInt(e.target.value) || 0)
                }
              />
            </div>
          </div>

          <ResponsiveDialogFooter className="px-4 sm:px-0">
            <Button
              type="submit"
              variant="gradient"
              disabled={form.processing}
            >
              {form.processing
                ? "Saving..."
                : isEditing
                  ? "Save Changes"
                  : "Create Category"}
            </Button>
          </ResponsiveDialogFooter>
        </form>
      </ResponsiveDialogContent>
    </ResponsiveDialog>
  )
}

export default function CourseCategoriesIndex({
  currentUser,
  categories,
  pendingCount,
}) {
  const [dialogOpen, setDialogOpen] = useState(false)
  const [editingCategory, setEditingCategory] = useState(null)
  const [dialogKey, setDialogKey] = useState(0)
  const [deleteTarget, setDeleteTarget] = useState(null)

  const [sortKey, setSortKey] = useState("position")
  const [direction, setDirection] = useState("asc")

  const handleSort = (column) => {
    if (sortKey !== column) {
      setSortKey(column)
      setDirection("asc")
      return
    }
    if (direction === "asc") {
      setDirection("desc")
      return
    }
    setSortKey("position")
    setDirection("asc")
  }

  const sortedCategories = useMemo(() => {
    const arr = [...(categories || [])]
    arr.sort((a, b) => {
      let cmp = 0
      if (sortKey === "position") {
        cmp = (a.position ?? 0) - (b.position ?? 0)
      } else if (sortKey === "name") {
        cmp = (a.name || "").localeCompare(b.name || "", undefined, { sensitivity: "base" })
      } else if (sortKey === "slug") {
        cmp = (a.slug || "").localeCompare(b.slug || "")
      }
      if (cmp !== 0) return direction === "asc" ? cmp : -cmp
      return (a.name || "").localeCompare(b.name || "", undefined, { sensitivity: "base" })
    })
    return arr
  }, [categories, sortKey, direction])

  const handleEdit = (category) => {
    setEditingCategory(category)
    setDialogKey((k) => k + 1)
    setDialogOpen(true)
  }

  const handleCreate = () => {
    setEditingCategory(null)
    setDialogKey((k) => k + 1)
    setDialogOpen(true)
  }

  const handleDelete = () => {
    if (deleteTarget) {
      router.delete(`/app/admin/course_categories/${deleteTarget.id}`, {
        preserveScroll: true,
        onSuccess: () => setDeleteTarget(null),
      })
    }
  }

  return (
    <AdminLayout currentUser={currentUser} pendingCount={pendingCount}>
      <Head title="Course Categories - Admin - Kingdom Factor" />

      <div className="space-y-6">
        <SectionTabs tabs={coursesTabs} />

        <div className="flex items-center justify-between">
          <div>
            <h1 className="font-display text-2xl font-bold text-zinc-900">
              Course Categories
            </h1>
            <p className="text-sm text-zinc-500 mt-1">
              Manage course categories for the LMS
            </p>
          </div>
          <Button variant="gradient" onClick={handleCreate}>
            <Plus className="h-4 w-4 mr-2" />
            Add Category
          </Button>
        </div>

        <Card>
          <Table>
            <TableHeader>
              <TableRow>
                <SortableHeader
                  column="position"
                  sortKey={sortKey}
                  direction={direction}
                  onSort={handleSort}
                  className="w-12 sm:w-16"
                >
                  #
                </SortableHeader>
                <SortableHeader
                  column="name"
                  sortKey={sortKey}
                  direction={direction}
                  onSort={handleSort}
                >
                  Name
                </SortableHeader>
                <SortableHeader
                  column="slug"
                  sortKey={sortKey}
                  direction={direction}
                  onSort={handleSort}
                  className="hidden sm:table-cell"
                >
                  Slug
                </SortableHeader>
                <TableHead className="text-right">Actions</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {sortedCategories.map((category) => (
                <TableRow key={category.id}>
                  <TableCell className="font-mono text-sm text-zinc-500">
                    {category.position}
                  </TableCell>
                  <TableCell className="font-medium whitespace-normal">
                    <div className="min-w-0">
                      <p className="line-clamp-1">{category.name}</p>
                      <p className="text-xs text-zinc-500 font-mono truncate sm:hidden mt-0.5">
                        {category.slug}
                      </p>
                    </div>
                  </TableCell>
                  <TableCell className="text-sm text-zinc-500 hidden sm:table-cell">
                    {category.slug}
                  </TableCell>
                  <TableCell className="text-right">
                    <div className="flex items-center justify-end gap-1">
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => handleEdit(category)}
                        title="Edit category"
                        aria-label={`Edit ${category.name}`}
                      >
                        <Pencil className="h-4 w-4" />
                      </Button>
                      <Button
                        variant="ghost"
                        size="sm"
                        className="text-red-600 hover:text-red-700 hover:bg-red-50"
                        onClick={() => setDeleteTarget(category)}
                        title="Delete category"
                        aria-label={`Delete ${category.name}`}
                      >
                        <Trash2 className="h-4 w-4" />
                      </Button>
                    </div>
                  </TableCell>
                </TableRow>
              ))}
              {sortedCategories.length === 0 && (
                <TableRow>
                  <TableCell
                    colSpan={4}
                    className="text-center py-8 text-zinc-500"
                  >
                    No course categories yet. Click "Add Category" to create
                    one.
                  </TableCell>
                </TableRow>
              )}
            </TableBody>
          </Table>
        </Card>
      </div>

      <CategoryDialog
        key={dialogKey}
        open={dialogOpen}
        onOpenChange={setDialogOpen}
        category={editingCategory}
      />

      <AlertDialog
        open={!!deleteTarget}
        onOpenChange={(open) => !open && setDeleteTarget(null)}
      >
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Delete Course Category</AlertDialogTitle>
            <AlertDialogDescription>
              Are you sure you want to delete "{deleteTarget?.name}"? This
              action cannot be undone. Categories with existing courses cannot
              be deleted.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction
              className="bg-red-600 text-white hover:bg-red-700"
              onClick={handleDelete}
            >
              Delete
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </AdminLayout>
  )
}
